import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Divider,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createRoom, updateRoom } from "../../services/WarehouseSettings";
import { IRoomRequestDTO } from "../../interfaces/WarehouseSettings";
import { formFieldStyles, labelStyles, dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import { useAuth } from "../../auth/AuthProvider";

interface AddEditRoomProps {
  open: boolean;
  onClose: () => void;
  editData: any;
  buildingId: string;
  floorId: string;
  onSuccess: (message: string) => void;
}

const AddEditRoom: React.FC<AddEditRoomProps> = ({
  open,
  onClose,
  editData,
  buildingId,
  floorId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    roomName: "",
    roomType: "",
    lengthMtr: "",
    widthMtr: "",
    heightMtr: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const WAREHOUSE_ID = loggedInUser?.workspaceId;
  const COMPANY_ID = loggedInUser?.companyId;

  useEffect(() => {
    if (editData && open) {
      
      const newFormData = {
        roomName: editData.roomName || editData.name || "",
        roomType: editData.roomType || editData.type || "",
        lengthMtr: editData.lengthMtr || editData.length || "",
        widthMtr: editData.widthMtr || editData.width || "",
        heightMtr: editData.heightMtr || editData.height || "",
      };
      
      console.log("Setting form data to:", newFormData);
      setFormData(newFormData);
    } else if (!editData) {
      console.log("Resetting form data for new room");
      setFormData({
        roomName: "",
        roomType: "",
        lengthMtr: "",
        widthMtr: "",
        heightMtr: "",
      });
    }
    setErrors({});
  }, [editData, open]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.roomName) newErrors.roomName = "Room name is required";
    if (!formData.roomType) newErrors.roomType = "Room type is required";
    if (!formData.lengthMtr) newErrors.lengthMtr = "Length is required";
    else if (Number(formData.lengthMtr) <= 0) newErrors.lengthMtr = "Length must be greater than 0";
    if (!formData.widthMtr) newErrors.widthMtr = "Width is required";
    else if (Number(formData.widthMtr) <= 0) newErrors.widthMtr = "Width must be greater than 0";
    if (!formData.heightMtr) newErrors.heightMtr = "Height is required";
    else if (Number(formData.heightMtr) <= 0) newErrors.heightMtr = "Height must be greater than 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!COMPANY_ID || !WAREHOUSE_ID) {
      setSnackbarMessage("User information not available");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    setLoading(true);
    try {
      const isEdit = Boolean(editData?.id);
      
      const dto: IRoomRequestDTO = {
        companyId: COMPANY_ID,
        warehouseId: WAREHOUSE_ID,
        buildingId: Number(buildingId),
        floorId: Number(floorId),
        roomName: formData.roomName,
        roomType: formData.roomType,
        lengthMtr: Number(formData.lengthMtr),
        widthMtr: Number(formData.widthMtr),
        heightMtr: Number(formData.heightMtr),
      };

      const response = isEdit 
        ? await updateRoom(editData.id, dto)
        : await createRoom(dto);

      if (response && response.status === "success") {
        const message = isEdit ? "Room updated successfully" : "Room added successfully";
        onSuccess(message);
        onClose();
      } else {
        const errorMsg = response?.errorResponse?.response?.data?.message || 
                         response?.errorResponse?.message || 
                         `Error ${isEdit ? 'updating' : 'creating'} room`;
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSnackbarMessage(err.message || err);
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography sx={dialogTitleStyles}>
            {editData ? "Edit Room" : "Add Room"}
          </Typography>
          <Typography sx={dialogDescriptionStyles}>
            {editData ? "Edit Room" : "Add Room"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>
              Room Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="eg. Cold Storage Room"
              value={formData.roomName}
              onChange={(e) => {
                setFormData({ ...formData, roomName: e.target.value });
                setErrors({ ...errors, roomName: "" });
              }}
              error={!!errors.roomName}
              helperText={errors.roomName}
              sx={formFieldStyles}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>
              Room Type <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              select
              value={formData.roomType}
              onChange={(e) => {
                setFormData({ ...formData, roomType: e.target.value });
                setErrors({ ...errors, roomType: "" });
              }}
              error={!!errors.roomType}
              helperText={errors.roomType}
              sx={formFieldStyles}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" disabled>Select Type</MenuItem>
              <MenuItem value="Temporary_Storage">Temporary Storage</MenuItem>
              <MenuItem value="Item_Storage">Item Storage</MenuItem>
              <MenuItem value="Delivery_Packing">Delivery Packing</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography component="label" sx={labelStyles}>
              Dimension (L x W x H m) <span style={{ color: "red" }}>*</span>
            </Typography>
          </Grid>
          
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="5"
              value={formData.lengthMtr}
              onChange={(e) => {
                setFormData({ ...formData, lengthMtr: e.target.value });
                setErrors({ ...errors, lengthMtr: "" });
              }}
              error={!!errors.lengthMtr}
              helperText={errors.lengthMtr}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="4"
              value={formData.widthMtr}
              onChange={(e) => {
                setFormData({ ...formData, widthMtr: e.target.value });
                setErrors({ ...errors, widthMtr: "" });
              }}
              error={!!errors.widthMtr}
              helperText={errors.widthMtr}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="3"
              value={formData.heightMtr}
              onChange={(e) => {
                setFormData({ ...formData, heightMtr: e.target.value });
                setErrors({ ...errors, heightMtr: "" });
              }}
              error={!!errors.heightMtr}
              helperText={errors.heightMtr}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider sx={{ml:2, mr:2}}/>
      <DialogActions sx={{ ml:1, mr:1, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
            padding: "8px 16px",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            padding: "8px 40px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : (editData ? "Save" : "Add")}
        </Button>
      </DialogActions>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddEditRoom;